import React, { useMemo, useState } from 'react'
import styles from "../css/Login.module.css"
import { IoMailOutline } from "react-icons/io5";
import { FiLock } from "react-icons/fi";
import { IoEyeOffOutline } from "react-icons/io5";
import { IoEyeOutline } from "react-icons/io5";
import { IoWarning } from "react-icons/io5";
import { IoMdCloseCircle } from "react-icons/io";
import LoadingButton from '../../Loaders/js/LoadingButton';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import validator from 'validator';
import { useDispatch } from 'react-redux';
import { login } from '../../../actions/authActions';
import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import { AUTH_RESET } from '../../../actions/authTypes';
import AuthScreen from './AuthScreen';
import MobileHeader from './MobileHeader';
import { SignInFloatingCards } from './FloatingCards';
import LogoItem from '../../Logo/LogoItem';
import { BsCheck } from 'react-icons/bs';

const Login = () => {
  const [data,setData] = useState({email:"",password:"",rememberMe:false})
  const [showPassword,setShowPassword] = useState(false)
  const [emailError,setEmailError] = useState("")
  const [crashError,setCrashError] = useState(false)
  const [authError,setAuthError] = useState("")
  const loading = useSelector(state => state.authState.loading)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation();
  const [successMessage,setSuccessMessage] = useState(location.state?.successMessage ?? "")

  useEffect(() => {
    if (successMessage) {
        window.history.replaceState({}, "")
    }
  }, [successMessage])

  useEffect(()=>{
    return () => {
      dispatch({type: AUTH_RESET})
    }
  },[dispatch])

  const handleSubmit = async(e) => {
    setCrashError("")
    setSuccessMessage("")
    e.preventDefault()
    if (!validator.isEmail(data.email)){
      setEmailError("Please enter a valid email address.")
      return
    }
    try{
      await dispatch(login(data))
      navigate('/notes')
    }
    catch(error){
      if (error?.response?.status === 400){
        setAuthError("Incorrect Credentials")
      }
      else{
        setCrashError("Something unexpected occured")
      }
    }
  }

  const handleChange = (e) => {
    let name = e.target.name
    let value = e.target.value
    setData({...data,[name]:value})
    if (authError){
      setAuthError("")
    }
    if (emailError && name === "email"){
      setEmailError("")
    }
  }

  const clearError = () => {
    if (authError){
      setAuthError("")
    }
    else{
      setCrashError("")
    }
  }

  const isDisabled = useMemo(() => {
    if (loading) return true
    if (!data.email.trim() || !data.password || emailError){
      return true
    }
    else{
      return false
    }
  },[data,emailError,loading])

  return (
    <AuthScreen
      form = {
        <div className = {styles.box}>
          <MobileHeader title = "Sign in" subtitle = "Welcome Back - Enter your details to access your notes"/>
          <div className = {styles.topNav}>
            <div className = {styles.appRep}><LogoItem/></div>
            <p className = {styles.navLink}>Don't have an account? <NavLink to = "/signup">Sign up</NavLink></p>
          </div>
          <main>
            <div className = {styles.title}>
              <div className = {styles.mainTitle}>Sign in</div>
              <div className = {styles.instructions}>Welcome Back - Enter your details to access your notes</div>
            </div>
            <form onSubmit = {handleSubmit} autoComplete = "off">
              {successMessage && <div data-testid = "reset-success-message" className = {styles.success}>
                <div className = {styles.successInfo}>
                  <div className = {styles.checkIcon}><BsCheck/></div>
                  {successMessage}
                </div>
                <div className = {styles.closeIcon} onClick = {()=>setSuccessMessage("")}><IoMdCloseCircle/></div>
              </div>}
              {(crashError || authError) && <div data-testid = {crashError ? "crash-error" : "auth-error"} className = {styles.error}>
                <div className = {styles.errorInfo}>
                  <div className = {styles.warningIcon}><IoWarning/></div>
                  {crashError ? crashError : authError}
                </div>
                <div className = {styles.closeIcon} onClick = {()=>clearError()}><IoMdCloseCircle/></div>
              </div>}
              <div className = {styles.inputs}>
                <div className = {styles.inputWrapper}>
                  <label htmlFor='email'>Email</label>
                  <div className= {`${styles.inputBox} ${emailError ? styles.errorBox : ""}`}>
                    <div className = {styles.emailIcon}><IoMailOutline/></div>
                    <div className = {styles.inputContainer}>
                      <input
                        type = "text"
                        value = {data.email}
                        name = "email"
                        onChange = {handleChange}
                        placeholder = "Enter your email"
                        autoComplete = "off"
                      />
                    </div>
                  </div>
                  {emailError && <div data-testid = "email-error" className = {styles.inputError}>{emailError}</div>}
                </div>
                <div className = {styles.inputWrapper}>
                  <label htmlFor='password'>Password</label>
                  <div className={styles.inputBox}>
                    <div className = {styles.passwordIcon}><FiLock/></div>
                    <div className = {styles.inputContainer}>
                      <input
                        type = {showPassword ? "text" : "password"}
                        value = {data.password}
                        name = "password"
                        onChange = {handleChange}
                        placeholder = "Enter your password"
                        autoComplete = "off"
                      />
                    </div>
                    <div className = {styles.visibilityIcon} onClick = {()=>setShowPassword(!showPassword)}>{showPassword ? <IoEyeOffOutline/> : <IoEyeOutline/>}</div>
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.rememberWrap}>
                    <input type="checkbox" id="rem" onChange = {(e)=>setData({...data,rememberMe: e.target.checked})}/>
                    <label htmlFor="rem">Remember me</label>
                  </div>
                  <NavLink to = "/forgot-password" className={styles.forgotLink}>Forgot password?</NavLink>
                </div>
              </div>
              <button type = "submit" className = {loading ? styles.loading : ""} disabled = {isDisabled}>
                Sign In
                <div className = {styles.arrowCircle}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </div>
                {loading && <LoadingButton/>}
              </button>
              <div className = {styles.linkWrapper}>Not Registered yet?<NavLink to = "/signup">Sign up</NavLink></div>
            </form>
          </main>
        </div>
        }
        floatingContent={<SignInFloatingCards/>}
      />
  )
}

export default Login